import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileCsv, faFileExcel, faFilePdf, faRotate } from '@fortawesome/free-solid-svg-icons';
import { api } from '../../lib/api/client';
import { useIsSiteAdmin } from '../../lib/api/hooks';
import { toCsv } from '../../lib/reports/csv';
import { toXlsx } from '../../lib/reports/xlsx';
import { downloadBlob, printDocument } from '../../lib/reports/download';
import {
  DOCE_EXPORT_COLUMNS,
  doceReportHtml,
  oneLineAddress,
  prettyUrl,
  printedDate,
  reportFilename,
  type DoceReportResponse,
  type DoceRow,
} from '../../lib/reports/doceCustomers';

/**
 * Customers with an active FBI Departmental Order subscription.
 *
 * Read live from Stripe rather than from our own Subscription rows: a row written by JIT migration while
 * Stripe was unreachable carries no captureTypes, so a local read would omit exactly the customers whose
 * records we are least sure of. The rules live in `services/lib/domain/doceReport.ts` and are shared with
 * `npm run -w services doce-customers`, so the console and the script cannot come to disagree about who
 * is on the list.
 */

const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export function DoceCustomers() {
  const isAdmin = useIsSiteAdmin();
  /*
   * On by default, and it governs the EXPORTS as well as the table.
   *
   * Our own accounts hold DOCE subscriptions for testing. They are shown — an admin is entitled to the
   * whole list and should be able to see why a count differs — but a document that leaves this room is a
   * list of CUSTOMERS, and ours have no business being read out as one of them.
   */
  const [hideInternal, setHideInternal] = useState(true);
  const [q, setQ] = useState('');
  const [blocked, setBlocked] = useState(false);

  const report = useQuery({
    queryKey: ['admin-doce-report'],
    queryFn: () => api.get<DoceReportResponse>('/admin/reports/doce'),
    enabled: isAdmin,
    // Cached in the Lambda too. A subscription list does not change by the minute, and this page is
    // opened to be read and exported rather than watched.
    staleTime: 5 * 60_000,
  });

  const all = report.data?.rows ?? [];
  const generatedOn = report.data?.generatedOn ?? '';

  /** What the table shows and what an export writes — deliberately the same rows. */
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return all.filter((r) => {
      if (hideInternal && r.internal) return false;
      if (!needle) return true;
      return [r.name, r.email, r.city, r.state, r.stripeCustomerId]
        .some((v) => (v ?? '').toLowerCase().includes(needle));
    });
  }, [all, hideInternal, q]);

  const filename = reportFilename(generatedOn);

  const exportCsv = () =>
    downloadBlob(toCsv(shown, DOCE_EXPORT_COLUMNS), `${filename}.csv`, 'text/csv;charset=utf-8');

  const exportXlsx = () =>
    downloadBlob(
      toXlsx(shown, DOCE_EXPORT_COLUMNS, { sheetName: 'DOCE customers' }),
      `${filename}.xlsx`,
      XLSX_TYPE,
    );

  const exportPdf = () => setBlocked(!printDocument(doceReportHtml(shown, generatedOn)));

  if (!isAdmin) {
    return (
      <div className="panel empty">
        <h3>Restricted</h3>
        <p>This report is available to Lakota platform staff only.</p>
      </div>
    );
  }

  /*
   * Counted from the rows on screen, not from the server's totals.
   *
   * The server counts every row it found; the page may be hiding our own accounts or filtering by name.
   * A summary that disagrees with the table under it is worse than no summary — someone will quote the
   * number and the list will not support it.
   */
  const facts = {
    withoutAccount: shown.filter((r) => !r.accountId).length,
    withoutWebsite: shown.filter((r) => !r.website).length,
    withoutAddress: shown.filter((r) => !r.line1 && !r.city).length,
  };
  const internalCount = all.filter((r) => r.internal).length;
  const hiddenCount = all.length - shown.length;

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>DOCE customers</h1>
          <div className="sub">
            Everyone with an active subscription to submit FBI Departmental Order transactions, read live
            from Stripe.
          </div>
        </div>
        <div className="rpt-actions">
          <button className="btn btn-ghost btn-sm" onClick={() => report.refetch()} disabled={report.isFetching}>
            <FontAwesomeIcon icon={faRotate} spin={report.isFetching} /> Refresh
          </button>
          {/* Disabled on an empty result rather than hidden: a button that vanishes reads as a bug. */}
          <button className="btn btn-sm" onClick={exportPdf} disabled={!shown.length}>
            <FontAwesomeIcon icon={faFilePdf} /> PDF
          </button>
          <button className="btn btn-sm" onClick={exportXlsx} disabled={!shown.length}>
            <FontAwesomeIcon icon={faFileExcel} /> Excel
          </button>
          <button className="btn btn-sm" onClick={exportCsv} disabled={!shown.length}>
            <FontAwesomeIcon icon={faFileCsv} /> CSV
          </button>
        </div>
      </div>

      {blocked && (
        <p className="cart-error">
          Allow pop-ups for this site to print the report. Excel and CSV download without one.
        </p>
      )}

      {report.isPending ? (
        <p className="loading-screen">// reading subscriptions…</p>
      ) : report.isError ? (
        <div className="panel empty">
          <h3>Could not build the report</h3>
          <p>Stripe could not be reached. Try again in a moment.</p>
        </div>
      ) : (
        <>
          <div className="panel rpt-summary">
            <div className="cust-fact">
              <span className="cust-fact-num">{shown.length}</span>
              <span className="cust-fact-lbl">Customers shown</span>
            </div>
            <div className="cust-fact">
              <span className="cust-fact-num">{facts.withoutAccount}</span>
              <span className="cust-fact-lbl">No EFTCreator account yet</span>
            </div>
            <div className="cust-fact">
              <span className="cust-fact-num">{facts.withoutWebsite}</span>
              <span className="cust-fact-lbl">No website recorded</span>
            </div>
            <div className="cust-fact">
              <span className="cust-fact-num">{facts.withoutAddress}</span>
              <span className="cust-fact-lbl">No address at all</span>
            </div>
          </div>

          <div className="panel rpt-controls">
            <input
              className="input"
              placeholder="Filter by name, email, city or Stripe id"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <label className="rpt-toggle">
              <input
                type="checkbox"
                checked={hideInternal}
                onChange={(e) => setHideInternal(e.target.checked)}
              />
              Hide our own accounts
              {internalCount ? <span className="rpt-dim"> ({internalCount})</span> : null}
            </label>
            {/* The exports write exactly these rows, so what is being left out has to be visible. */}
            {hiddenCount > 0 && (
              <span className="rpt-dim">{hiddenCount} row{hiddenCount === 1 ? '' : 's'} not shown or exported</span>
            )}
          </div>

          <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
            <table className="tx-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Physical address</th>
                  <th>Website</th>
                  <th>Plan</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.stripeCustomerId}>
                    <td>
                      <div className="rpt-cust">
                        {r.name}
                        {r.internal && <span className="pill">Ours</span>}
                      </div>
                      {r.email && <div className="rpt-dim">{r.email}</div>}
                    </td>
                    <td>
                      {oneLineAddress(r)}
                      {/* Said plainly, because a billing address is often an accountant's, not the premises. */}
                      {r.source === 'stripe' && (
                        <div className="rpt-dim">billing address on file — no EFTCreator account yet</div>
                      )}
                    </td>
                    <td>
                      {r.website ? (
                        <a href={r.website} target="_blank" rel="noreferrer noopener">{prettyUrl(r.website)}</a>
                      ) : (
                        <span className="rpt-none">—</span>
                      )}
                    </td>
                    <td>
                      {r.planName}
                      <div className="rpt-dim">{r.billing}</div>
                    </td>
                  </tr>
                ))}
                {!shown.length && (
                  <tr>
                    <td colSpan={4} className="rpt-dim">
                      {all.length ? 'Nothing matches that filter.' : 'No active DOCE subscriptions found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {generatedOn && <p className="rpt-dim rpt-foot">Read from Stripe {printedDate(generatedOn)}.</p>}
        </>
      )}
    </div>
  );
}

export type { DoceRow };
